import { endpoints, authHeaders, type TokenProvider } from '../config/api';
import type {
  ManagedIdentityUpsertPayload,
  ManagedIdentityResponseRecord,
} from '../types/trapi';

// Re-exported for back-compat with any existing `import type { TokenProvider }
// from './tmds'` consumers; the canonical declaration, and the shared
// `authHeaders` helper used below, now live in config/api.ts so this client
// and tokenusage.ts no longer carry their own near-identical copies (see PR
// #96968 thread 974825).
export type { TokenProvider };

export type IdCheckOk = { name: string; type: string; objectId: string };

async function toJson<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`HTTP ${res.status} ${res.statusText}${text ? ` - ${text}` : ''}`);
  }
  if (res.status === 204) {
    return undefined as unknown as T;
  }
  return (await res.json()) as T;
}

export function createTmdsClient(getToken: TokenProvider) {
  return {
    async getMyAccess<T = unknown>(): Promise<T> {
      const res = await fetch(endpoints.myaccess(), {
        method: 'GET',
        headers: await authHeaders(getToken),
      });
      return toJson<T>(res);
    },

    async getModels<T = unknown>(): Promise<T> {
      const res = await fetch(endpoints.models(), {
        method: 'GET',
        headers: await authHeaders(getToken),
      });
      return toJson<T>(res);
    },

    async getExamplesMapping<T = unknown>(): Promise<T> {
      const res = await fetch(endpoints.examples(), {
        method: 'GET',
        headers: await authHeaders(getToken),
      });
      return toJson<T>(res);
    },

    async postExample(options: {
      capability: string;
      apiVersion?: string;
      language?: string;
      example?: string;
      display?: 'online' | 'download';
      model?: string;
      apiPath?: string;
    }): Promise<{
      text?: string;
      fileName?: string;
      headers: Record<string, string>;
      blob?: Blob;
      status: number;
    }> {
      const body: Record<string, unknown> = {
        capability: options.capability,
      };
      if (options.apiVersion) body['api-version'] = options.apiVersion;
      if (options.language) body.language = options.language;
      if (options.example) body.example = options.example;
      if (options.display === 'online') body.display = 'online';
      if (options.model) body.model = options.model;
      if (options.apiPath) body.apiPath = options.apiPath;

      const res = await fetch(endpoints.examples(), {
        method: 'POST',
        headers: await authHeaders(getToken),
        body: JSON.stringify(body),
      });

      const headers: Record<string, string> = {};
      res.headers.forEach((v, k) => {
        headers[k] = v;
      });

      if (!res.ok) {
        let msg = '';
        try {
          msg = await res.text();
        } catch {
          // ignore: best-effort attempt to read response text
        }
        throw new Error(`HTTP ${res.status} ${res.statusText}${msg ? ` - ${msg}` : ''}`);
      }

      if (options.display === 'online') {
        const text = await res.text();
        return { text, headers, status: res.status };
      }

      const blob = await res.blob();
      // Try to derive filename
      let fileName: string | undefined;
      const cd = res.headers.get('content-disposition') || '';
      const m = cd.match(/filename="([^"]+)"/i);
      if (m) fileName = m[1];
      if (!fileName) {
        const cap = options.capability || 'example';
        const ver = options.apiVersion || 'fallback';
        const lang = options.language || 'code';
        fileName = `${cap}-${ver}-${lang}.txt`;
      }
      return { blob, fileName, headers, status: res.status };
    },

    async getMSI<T = unknown>(options?: { apiPath?: string }): Promise<T> {
      const res = await fetch(endpoints.managemsi(), {
        method: 'GET',
        headers: await authHeaders(
          getToken,
          options?.apiPath ? { apiPath: options.apiPath } : undefined,
        ),
      });
      return toJson<T>(res);
    },

    /**
     * Typed managemsi list. Dual-reads the Phase-3 display DTO (ownerDisplay) and
     * the legacy Phase-2 OID shape; both are members of ManagedIdentityResponseRecord.
     * Normalizes the payload to an array via a runtime guard (no any/unknown casts).
     */
    async getManagedIdentities(options?: { apiPath?: string }): Promise<ManagedIdentityResponseRecord[]> {
      const data = await this.getMSI<ManagedIdentityResponseRecord[] | null>(options);
      return Array.isArray(data) ? data : [];
    },

    async upsertMSI<T = unknown>(options: { apiPath: string; payload: ManagedIdentityUpsertPayload }): Promise<T> {
      /**
       * Upsert Managed Identity record.
       * Accepts legacy `owner` or canonical `owners` and normalizes to `owners[]`.
       * `primaryOwnerId` is sent as the selected UPN/email (lowercase); backend resolves to OID.
       */
      const raw = options.payload || {};
      const {
        id,
        objectId,
        owner,
        owners,
        primaryOwnerId,
        isUpdate,
        ...rest
      } = raw;

      const parseOwners = (input: unknown): string[] | undefined => {
        if (input == null) return undefined;
        const dedupe = (vals: string[]) =>
          Array.from(
            new Set(
              vals
                .map(v => (typeof v === 'string' ? v.trim() : ''))
                .filter(v => !!v)
                .map(v => v.toLowerCase())
            )
          );
        if (Array.isArray(input)) {
          return dedupe(input);
        }
        if (typeof input === 'string') {
          const trimmed = input.trim();
          if (!trimmed) return undefined;
          // Attempt parse as JSON array
          if (trimmed.startsWith('[')) {
            try {
              const parsed = JSON.parse(trimmed);
              if (Array.isArray(parsed)) {
                return dedupe(parsed);
              }
            } catch {
              // fall through
            }
          }
            // CSV support
          if (trimmed.includes(',')) {
            return dedupe(trimmed.split(','));
          }
          return dedupe([trimmed]);
        }
        return undefined;
      };

      const ownersArray = parseOwners(owners ?? owner);
      const normalizedPrimaryOwner = typeof primaryOwnerId === 'string' ? primaryOwnerId.trim().toLowerCase() : '';

      // For edits, send objectId instead of id; for creates omit objectId
      const resolvedObjectId = (objectId ?? id ?? '').trim();
      const body: Record<string, unknown> = resolvedObjectId ? { ...rest, objectId: resolvedObjectId } : { ...rest };
      if (ownersArray && ownersArray.length > 0) {
        body.owners = ownersArray;
      }
      if (normalizedPrimaryOwner) {
        body.primaryOwnerId = normalizedPrimaryOwner;
      }
      if (typeof isUpdate === 'boolean') {
        body.isUpdate = isUpdate;
      }

      const res = await fetch(endpoints.managemsi(), {
        method: 'POST',
        headers: await authHeaders(getToken, { apiPath: options.apiPath }),
        body: JSON.stringify(body),
      });
      return toJson<T>(res);
    },

    async deleteMSI(options: { apiPath: string; id: string }): Promise<void> {
      const body = {
        apiPath: options.apiPath,
        objectId: options.id,
      };
      const res = await fetch(endpoints.managemsi(), {
        method: 'DELETE',
        headers: await authHeaders(getToken),
        body: JSON.stringify(body),
      });
      await toJson(res);
    },

    /**
     * Remove one or more owners (owner-removal mode).
     * Do NOT include objectId header/body (signals owner-removal path).
     * Always send owner as array.
     */
    async deleteMSIByOwners(options: { apiPath: string; owners: string | string[] }): Promise<void> {
      const normalize = (o: string | string[]): string[] =>
        Array.from(
          new Set(
            (Array.isArray(o) ? o : [o])
              .map(v => v.trim())
              .filter(v => !!v)
              .map(v => v.toLowerCase())
          )
        );
      const ownersArray = normalize(options.owners);
      const body: Record<string, unknown> = {
        apiPath: options.apiPath,
        owner: ownersArray,
      };
      const res = await fetch(endpoints.managemsi(), {
        method: 'DELETE',
        headers: await authHeaders(getToken, { apiPath: options.apiPath }),
        body: JSON.stringify(body),
      });
      await toJson(res);
    },

    async getPathMappings<T = unknown>(): Promise<T> {
      const res = await fetch(endpoints.pathmappings(), {
        method: 'GET',
        headers: await authHeaders(getToken),
      });
      return toJson<T>(res);
    },

    async capacityCheck(apiId: string, model: string): Promise<{ available: boolean; message?: string }> {
      const res = await fetch(endpoints.capacitycheck(apiId, model), {
        method: 'POST',
        headers: await authHeaders(getToken),
        body: JSON.stringify({}),
      });
      return toJson(res);
    },

    async getTestConfig<T = unknown>(apiPath: string): Promise<T> {
      const res = await fetch(endpoints.testconfig(), {
        method: 'GET',
        headers: await authHeaders(getToken, {
          apiPath,
          globalProperty: 'globalRateLimits,accountModelLimits',
          'x-testconfig-includeDiff': 'true',
        }),
      });
      return toJson<T>(res);
    },

    async postRefresh(apiPath: string, reloadTokens: string[]): Promise<void> {
      const res = await fetch(endpoints.refresh(), {
        method: 'POST',
        headers: await authHeaders(getToken, { apiPath }),
        body: JSON.stringify({ reload: reloadTokens }),
      });
      await toJson(res);
    },

    async getTaskSuiteLatest<T = unknown>(task: string): Promise<T> {
      const res = await fetch(endpoints.taskSuiteLatest(task), {
        method: 'GET',
        headers: await authHeaders(getToken),
      });
      return toJson<T>(res);
    },

    async postTaskSuiteRun(task: string): Promise<{ jobId: string }> {
      const res = await fetch(`${endpoints.taskSuiteRun()}&task=${encodeURIComponent(task)}`, {
        method: 'POST',
        headers: await authHeaders(getToken),
        body: JSON.stringify({}),
      });
      return toJson(res);
    },

    async getTaskSuiteJobs<T = unknown>(): Promise<T> {
      const res = await fetch(endpoints.taskSuiteJobs(), {
        method: 'GET',
        headers: await authHeaders(getToken),
      });
      return toJson<T>(res);
    },

    async getTaskSuiteJob<T = unknown>(id: string): Promise<T> {
      const res = await fetch(endpoints.taskSuiteJob(id), {
        method: 'GET',
        headers: await authHeaders(getToken),
      });
      return toJson<T>(res);
    },

    async idCheck(id: string): Promise<{ status: number; data?: IdCheckOk; error?: string }> {
      const res = await fetch(endpoints.idcheck(id), {
        method: 'GET',
        headers: await authHeaders(getToken),
      });
      const status = res.status;

      if (res.ok) {
        try {
          const data = (await res.json()) as IdCheckOk;
          return { status, data };
        } catch {
          return { status, error: 'Malformed JSON response' };
        }
      }

      let error = '';
      try {
        const body = await res.json();
        if (body && typeof body === 'object' && 'error' in body) {
          const maybe = (body as { error?: unknown }).error;
            if (typeof maybe === 'string') {
              error = maybe;
            } else {
              error = JSON.stringify(body);
            }
        } else {
          error = JSON.stringify(body);
        }
      } catch {
        try {
          error = await res.text();
        } catch {
          error = '';
        }
      }
      return { status, error };
    },
  };
}
